import PopUp from "Components/PopUp/PopUp";
import RadioButtonGroup from "Components/RadioButtonGroup/RadioButtonGroup";
import { Button } from "Components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTrigger,
} from "Components/ui/dialog";
import { Separator } from "Components/ui/separator";
import { ArrowUpDown, CreditCard, Wallet } from "lucide-react";
import React, { useEffect, useState } from "react";
import bit from "../../../../../../Assets/Images/bitcoin.svg";
import usd from "../../../../../../Assets/Images/usd.png";
import { Input } from "Components/ui/input";
import TextInput from "Components/TextInput/TextInput";
import { useDispatch, useSelector } from "react-redux";
import { withdraw } from "../../../../../../Redux/Profile/ProfileActions";
import { useAuth } from "@clerk/clerk-react";
import { toast } from "sonner";
import bitLight from "../../../../../../Assets/Images/bitcoinLight.svg";
import { useTheme } from "hooks/ThemeContext";

function WithdrawPopup({
  open,
  setOpen,
  currencies,
  setCurrencies,
  selectedMethod,
  setSelectedMethod,
  value,
  setValue,
}) {
  const { theme } = useTheme();
  const [bankData, setBankData] = useState({});
  const [walletAddress, setWalletAddress] = useState();

  const dispatch = useDispatch();

  const { getToken } = useAuth();

  useEffect(() => {
    setBankData({
      recipientName: "",
      iban: "",
      bankName: "",
      swift: "",
    });
    setWalletAddress("");
  }, [selectedMethod]);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(
      withdraw(
        {
          type: selectedMethod === "bank" ? "BANK_TRANSFER" : "CRYPTO",
          currency: currencies,
          ...(selectedMethod === "crypto" && { walletAddress: walletAddress }),
          ...(selectedMethod === "bank" && {
            recipient: bankData.recipientName,
            iban: bankData.iban,
            bank: bankData.bankName,
            code: bankData.swift,
          }),
          amount: Number(value),
        },
        getToken
      )
    );
  };

  const { withdrawLoading, withdrawSuccess, withdrawError } = useSelector(
    (state) => state.ProfileReducer
  );

  useEffect(() => {
    if (withdrawSuccess) {
      toast.success("Withdraw request sent successfully!", {
        autoClose: 2000,
        className: "bg-green-opacity text-green",
      });
      setOpen(false);
    }
    if (withdrawError) {
      toast.error(withdrawError, {
        autoClose: 2000,
        className: "bg-red-opacity text-red",
      });
    }
  }, [withdrawSuccess, withdrawError]);

  return (
    <PopUp title={"Withdraw"} open={open} setOpen={setOpen}>
      <form
        className="w-full overflow-auto max-h-[70vh] hideScrollBar"
        onSubmit={(e) => handleSubmit(e)}
      >
        <div className="text-lg font-N_Roman mb-2">Method</div>
        <RadioButtonGroup
          noIcon
          grid={2}
          uniqueName={"paymentMethods"}
          data={[
            {
              label: (
                <div className="text-white flex flex-col gap-2">
                  <CreditCard />
                  <div>Bank Account</div>
                </div>
              ),
              value: "bank",
            },
            {
              label: (
                <div className="text-white  flex flex-col gap-2">
                  <img
                    src={theme === "dark" ? bit : bitLight}
                    alt="bitcoin"
                    className="w-[1.5rem]"
                  />
                  <div>Crypto</div>
                </div>
              ),
              value: "crypto",
            },
          ]}
          checked={selectedMethod}
          setChecked={(selected) => {
            setSelectedMethod(selected);
          }}
        />
        <div className="text-lg font-N_Roman mt-6 mb-2">Currency</div>
        <RadioButtonGroup
          grid={1}
          uniqueName={"currencies"}
          data={[
            {
              label: (
                <div className="text-white flex flex-row items-center gap-2">
                  <img src={usd} alt="usd" className="w-[1.5rem]" />
                  <div>{selectedMethod === "crypto" ? "USDT" : "USD"}</div>
                </div>
              ),
              value: "usd",
            },
          ]}
          checked={currencies}
          setChecked={(selected) => {
            setCurrencies(selected);
          }}
        />
        {selectedMethod === "bank" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-4 mt-4">
            <TextInput
              label="Recipient Name"
              required={true}
              placeholder="John Doe"
              type="text"
              value={bankData.recipientName}
              onChange={(e) =>
                setBankData({ ...bankData, recipientName: e.target.value })
              }
              className={"border-border-radio"}
              labelClassName={"text-lg font-N_Roman mt-4"}
            />
            <TextInput
              label="Bank Account Number (IBAN)"
              required={true}
              placeholder="Enter Bank Account Number"
              type="text"
              value={bankData.iban}
              onChange={(e) =>
                setBankData({ ...bankData, iban: e.target.value })
              }
              className={"border-border-radio"}
              labelClassName={"text-lg font-N_Roman mt-4"}
            />
            <TextInput
              label="Bank Name"
              required={true}
              placeholder="Enter Bank Name"
              type="text"
              value={bankData.bankName}
              onChange={(e) =>
                setBankData({ ...bankData, bankName: e.target.value })
              }
              className={"border-border-radio"}
              labelClassName={"text-lg font-N_Roman mt-4"}
            />
            <TextInput
              label="SWIFT/BIC Code"
              required={true}
              placeholder="AAAA-BB-CC-123"
              type="text"
              value={bankData.swift}
              onChange={(e) =>
                setBankData({ ...bankData, swift: e.target.value })
              }
              className={"border-border-radio"}
              labelClassName={"text-lg font-N_Roman mt-4"}
            />
          </div>
        ) : (
          selectedMethod === "crypto" && (
            <div className="mt-4">
              <TextInput
                label="Crypto wallet address"
                required={true}
                placeholder="Enter Wallet Address"
                type="text"
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                className={"border-border-radio"}
                labelClassName={"text-lg font-N_Roman mt-4"}
              />
            </div>
          )
        )}

        <TextInput
          label="Amount to Withdraw"
          required={true}
          placeholder="Enter Withdrawal Amount"
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={"border-border-radio"}
          labelClassName={"text-lg font-N_Roman mt-4"}
        />

        <Button
          className="mt-6 w-full"
          disabled={
            !value ||
            withdrawLoading ||
            !selectedMethod ||
            (selectedMethod === "bank" &&
              (!bankData.recipientName ||
                !bankData.iban ||
                !bankData.bankName ||
                !bankData.swift)) ||
            (selectedMethod === "crypto" && !walletAddress)
          }
        >
          {withdrawLoading ? "Loading..." : "Confirm Withdraw"}
        </Button>
      </form>
    </PopUp>
  );
}

export default WithdrawPopup;
